import { useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography, Accordion, AccordionSummary, AccordionDetails, TextField, MenuItem, Stack, Autocomplete,
} from "@mui/material";
import {
  TIPOS_OPORTUNIDAD,
  ETAPAS_EXPANSION,
  ORIGENES_OPORTUNIDAD,
  labelEtapaExpansion,
  normalizarOrigenOportunidad,
} from "./expansionConstants";

import { draftPatch, draftChanged } from "../services/crmDraft";
export default function OportunidadFormDialog({ open, onClose, store, editData = null, defaultLeadId = "", onNuevaVenta }) {
  const leads = store?.leads ?? [];
  const clientes = leads;

  const empty = useMemo(() => ({
    leadId: defaultLeadId,
    nombre: "",
    descripcion: "",
    tipo: "nueva_solucion",
    etapa: "interes_detectado",
    montoEstimado: "",
    origen: "",
    detalleOrigen: "",
    vendedorId: "",
    proximoSeguimiento: "",
    proximaAccion: "",
    fechaCierreEsperada: "",
    motivoPerdida: "",
  }), [defaultLeadId]);

  const [form, setForm] = useState(empty);
  const draftSession = useRef(null);
  const [draftError, setDraftError] = useState("");
  const liveOpportunity = editData ? store.oportunidadesExpansion?.find(o => o.id === editData.id) : null;

  useEffect(() => {
    if (!open) { draftSession.current = null; return; }
    if (draftSession.current?.id === (editData?.id || "new:" + defaultLeadId)) return;
    draftSession.current = { id: editData?.id || "new:" + defaultLeadId, base: editData };
    setDraftError("");
    if (open) {
      if (editData) {
        const { origen, detalleOrigen } = normalizarOrigenOportunidad(editData);
        setForm({
          ...editData,
          origen,
          detalleOrigen,
          montoEstimado: editData.montoEstimado ?? "",
          vendedorId: editData.vendedorId || "",
        });
      } else {
        setForm({ ...empty, leadId: defaultLeadId });
      }
    }
  }, [open, editData, defaultLeadId, empty]);

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = () => {
    if (!form.leadId || !form.nombre.trim() || !form.origen || (form.etapa === "perdida" && !form.motivoPerdida?.trim())) return;
    const monto = form.montoEstimado === "" || form.montoEstimado == null
      ? null
      : Math.round(parseFloat(form.montoEstimado) * 100) / 100;
    const payload = {
      ...form,
      montoEstimado: Number.isFinite(monto) && monto >= 0 ? monto : null,
      vendedorId: form.vendedorId || null,
      proximoSeguimiento: form.proximoSeguimiento || null,
    };
    if (editData) {
      try {
        const patch = draftPatch(draftSession.current.base, payload, liveOpportunity);
        if (patch.etapa === "ganada" && liveOpportunity.etapa !== "ganada") { onNuevaVenta?.(form.leadId, editData.id); onClose(); return; }
        if (patch.etapa) store.moverOportunidadEtapa(editData.id, patch.etapa, { motivoPerdida: payload.motivoPerdida });
        const { etapa, ...rest } = patch;
        if (Object.keys(rest).length) store.editarOportunidad(editData.id, rest);
      } catch (error) { setDraftError(error.message); return; }
    } else store.agregarOportunidad(payload);
    onClose();
  };

  const leadSel = clientes.find((l) => l.id === form.leadId) || null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle fontWeight={700}>{editData ? "Editar oportunidad" : "Nueva oportunidad"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Alert severity="info">Calificada: necesidad, decisión y presupuesto confirmados. Propuesta: oferta enviada. Negociación: condiciones en discusión. Ganada: venta registrada.</Alert>
          <Autocomplete
            options={clientes}
            value={leadSel}
            onChange={(_, l) => setForm((f) => ({ ...f, leadId: l?.id || "", vendedorId: l?.vendedorId || "", tipo: (store.ventas || []).some(v => v.leadId === l?.id && !v.anulada) ? "nueva_solucion" : "primera_venta" }))}
            getOptionLabel={(l) => l.nombre}
            renderInput={(params) => <TextField {...params} label="Cliente o prospecto" size="small" required />}
            disabled={!!defaultLeadId && !editData}
          />
          <TextField label="Nombre de la oportunidad" value={form.nombre} onChange={handleChange("nombre")} required fullWidth size="small" />
          <TextField label="Descripción" value={form.descripcion} onChange={handleChange("descripcion")} multiline rows={2} fullWidth size="small" />
          <TextField select label="Tipo" value={form.tipo} onChange={handleChange("tipo")} fullWidth size="small">
            {TIPOS_OPORTUNIDAD.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
          </TextField>
          {editData && (
            <TextField select label="Etapa" value={form.etapa} onChange={handleChange("etapa")} fullWidth size="small">
              {ETAPAS_EXPANSION.map((e) => <MenuItem key={e} value={e}>{labelEtapaExpansion(e)}</MenuItem>)}
            </TextField>
          )}
          <TextField
            label={`Monto estimado (${store.config.moneda}) — opcional`}
            value={form.montoEstimado}
            onChange={handleChange("montoEstimado")}
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            fullWidth
            size="small"
            helperText="Dejar vacío si aún no hay estimación"
          />
          <TextField
            select
            label="Origen de la oportunidad"
            value={form.origen}
            onChange={handleChange("origen")}
            fullWidth
            size="small"
            required
            helperText="Qué originó este negocio concreto"
          >
            <MenuItem value="" disabled>Seleccionar origen</MenuItem>
            {ORIGENES_OPORTUNIDAD.map((o) => (
              <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Detalle (opcional)"
            value={form.detalleOrigen || ""}
            onChange={handleChange("detalleOrigen")}
            fullWidth
            size="small"
            helperText="Ej. pidió app móvil después de comprar 2 sistemas"
          />
          <TextField label="Cierre esperado" type="date" value={form.fechaCierreEsperada || ""} onChange={handleChange("fechaCierreEsperada")} InputLabelProps={{ shrink: true }} size="small" />
          {form.etapa === "perdida" && <TextField label="Motivo de pérdida" required value={form.motivoPerdida || ""} onChange={handleChange("motivoPerdida")} size="small" />}
          <TextField label="Próxima acción" value={form.proximaAccion} onChange={handleChange("proximaAccion")} fullWidth size="small" />
          <TextField label="Próximo seguimiento" type="date" value={form.proximoSeguimiento || ""} onChange={handleChange("proximoSeguimiento")}
            InputLabelProps={{ shrink: true }} fullWidth size="small" />
          {store.config.vendedores.length > 0 && (
            <TextField select label="Responsable" disabled={store.isOwner === false} value={form.vendedorId} onChange={handleChange("vendedorId")} fullWidth size="small">
              <MenuItem value="">Sin asignar</MenuItem>
              {store.config.vendedores.filter((v) => v.activo).map((v) => (
                <MenuItem key={v.id} value={v.id}>{v.nombre}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        {editData && <Accordion sx={{ mt: 2 }}><AccordionSummary>Historial de la oportunidad</AccordionSummary><AccordionDetails>{(editData.historialEtapas || []).map((h, i) => <Typography variant="body2" key={"stage" + i}>{h.fechaHora?.slice(0, 16).replace("T", " ")} · {h.etapaAnterior ? labelEtapaExpansion(h.etapaAnterior) + " → " : ""}{labelEtapaExpansion(h.etapaNueva)}{h.motivoPerdida ? " · " + h.motivoPerdida : ""}</Typography>)}{(editData.historialEdiciones || []).map((h, i) => <Typography variant="body2" key={"edit" + i}>{h.fechaHora?.slice(0, 16).replace("T", " ")} · {(h.cambios || []).map(c => c.campo + ": " + (c.valorAnterior ?? "—") + " → " + (c.valorNuevo ?? "—")).join("; ")}</Typography>)}</AccordionDetails></Accordion>}
      </DialogContent>
      {draftError && <Alert severity="warning">{draftError}</Alert>}
      {!draftError && draftChanged(draftSession.current?.base, liveOpportunity) && <Alert severity="info">La oportunidad cambió en otra sesión. Tu borrador se conserva.</Alert>}
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!form.leadId || !form.nombre.trim() || !form.origen || (form.etapa === "perdida" && !form.motivoPerdida?.trim())}>Guardar</Button>
      </DialogActions>
    </Dialog>
  );
}
